import React, { useCallback, useEffect, useRef, useState } from "react";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { MAIN_URL } from "../../../Configurations/Urls";
import {
  mergeToolbarFilters,
  ToolbarFilterSelect,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters";

const OPENING_TOOLBAR_FILTERS = [
  { urlKey: "opening_type", field: "opening_type" },
  { urlKey: "status", field: "status" },
];

const OPENING_TYPE_OPTIONS = [
  { value: "Employment", label: "Employment" },
  { value: "Internship", label: "Internship" },
  { value: "Apprenticeship", label: "Apprenticeship" },
  { value: "Trainee", label: "Trainee" },
];

const OPENING_STATUS_OPTIONS = [
  { value: "Draft", label: "Draft" },
  { value: "Open", label: "Open" },
  { value: "On Hold", label: "On Hold" },
  { value: "Closed", label: "Closed" },
  { value: "Cancelled", label: "Cancelled" },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_recruitment_opening_id",
  sortOrder: "desc",
};

const DEFAULT_COLUMNS = [
  { field: "opening_title", label: "Opening Title", visible: true, width: 220, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "opening_type", label: "Type", visible: true, width: 140, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "department_name", label: "Department", visible: true, width: 160, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "designation_name", label: "Designation", visible: true, width: 160, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "number_of_openings", label: "Openings", visible: true, width: 110, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "opening_date", label: "Opening Date", visible: true, width: 140, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "application_deadline", label: "Deadline", visible: true, width: 140, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "priority", label: "Priority", visible: true, width: 110, filterable: true, sortable: true, pinned: "none", required: false },
  { field: "status", label: "Status", visible: true, width: 120, filterable: true, sortable: true, pinned: "none", required: false },
];

const dash = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return value;
};

const sliceDate = (value) => (value ? String(value).slice(0, 10) : "—");

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    ) {
      return;
    }

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;
      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => {
            const trimmed = v.trim();
            return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
          });
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }
      filters.push({ field, operator, value: filterValue });
    }
  });

  const search = searchParams.get("search");
  if (search) {
    filters.push({
      field: "opening_title",
      operator: "contains",
      value: search,
    });
  }

  return filters;
};

const normalizeAppliedFilters = (filters) => {
  if (!Array.isArray(filters)) return [];

  return filters
    .filter((f) => f?.field && f?.operator)
    .map((f) => {
      const requiresValue = operatorRequiresValue(f.operator);
      if (!requiresValue) {
        return { field: f.field, operator: f.operator, value: null };
      }

      let value = f.value;
      if (value === null || value === undefined || value === "") return null;
      if (Array.isArray(value) && value.length === 0) return null;

      if (["equals", "not_equals"].includes(f.operator) && typeof value === "string") {
        value = value.split(",").map((v) => {
          const trimmed = v.trim();
          return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
        });
      } else if (
        ["equals", "not_equals"].includes(f.operator) &&
        Array.isArray(value)
      ) {
        value = value.map((v) => {
          const trimmed = String(v).trim();
          return /^\d+$/.test(trimmed) ? trimmed : trimmed.toLowerCase();
        });
      }

      return { field: f.field, operator: f.operator, value };
    })
    .filter(Boolean);
};

function RecruitmentOpeningsList() {
  const [rows, setRows] = useState([]);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }
      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (configRes.ok) {
          const configResponse = await configRes.json();
          const orgKey = `organization_recruitment_openings_grid_${org.organization_id}`;
          const savedConfig = (configResponse.datagrids || []).find(
            (dg) => dg.datagrid_key === orgKey
          );
          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);
            setConfigColumns(serverCfg?.columns?.length ? serverCfg.columns : DEFAULT_COLUMNS);
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error(error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };
    loadTableConfiguration();
  }, [org?.organization_id]);

  useEffect(() => {
    if (hasInitializedURL.current) return;
    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;
    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });
    if (needsUpdate) setSearchParams(params, { replace: true });
    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;
    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();
    setLoading(true);

    const fetchData = async () => {
      try {
        const requestBody = {
          filters: mergeToolbarFilters(
            appliedFilters !== null
              ? normalizeAppliedFilters(appliedFilters)
              : buildFiltersFromParams(searchParams),
            searchParams,
            OPENING_TOOLBAR_FILTERS
          ),
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field:
              searchParams.get("sortBy") ||
              "organization_recruitment_opening_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-openings-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data = [];
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page: result.pagination.page || 1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        setRows(
          data.map((item) => ({
            ...item,
            id: item?.organization_recruitment_opening_id,
            opening_title: dash(item?.opening_title),
            opening_type: dash(item?.opening_type),
            department_name: dash(item?.department_name),
            designation_name: dash(item?.designation_name),
            number_of_openings: dash(item?.number_of_openings),
            opening_date: sliceDate(item?.opening_date),
            application_deadline: sliceDate(item?.application_deadline),
            priority: dash(item?.priority),
            status: dash(item?.status),
          }))
        );
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error(error);
        toast.error("Failed to load recruitment openings");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => fetchControllerRef.current?.abort();
  }, [org?.organization_id, searchParams, appliedFilters]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-opening/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (response.ok) {
          const result = await response.json();
          toast.success(result.message || "Recruitment opening deleted");
          setAppliedFilters((prev) => (prev === null ? [] : [...prev]));
        } else {
          const errorData = await response.json();
          toast.error(errorData.message || "Failed to delete recruitment opening");
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to delete recruitment opening");
      }
    },
    [org?.organization_id]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/recruitment/openings/edit/${item.id || item.organization_recruitment_opening_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/recruitment/openings/view/${item.id || item.organization_recruitment_opening_id}`
      );
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback((nextFilters) => {
    setAppliedFilters(Array.isArray(nextFilters) ? nextFilters : []);
  }, []);

  return (
    <Layout4
      loading={false}
      heading={"Recruitment Openings"}
      btnName={"Add Recruitment Opening"}
      add_action={"RECRUITMENT_SOURCES_ADD"}
      Route={"/recruitment/openings"}
    >
      {!loadingConfig && (
        <TableDataGeneric
          tableName="organization_recruitment_openings"
          primaryKey="organization_recruitment_opening_id"
          heading="Recruitment Openings"
          data={rows}
          sortname="organization_recruitment_opening_id"
          showActions={true}
          Route="/recruitment/openings"
          DeleteFunc={handleDelete}
          EditFunc={handleEdit}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          onFilterChange={handleFilterChange}
          edit_delete_action={[
            "RECRUITMENT_SOURCES_DELETE",
            "RECRUITMENT_SOURCES_EDIT",
          ]}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="organization_recruitment_openings"
          toolbarExtras={
            <>
              <ToolbarFilterSelect
                label="Opening Type"
                urlKey="opening_type"
                allLabel="All opening types"
                options={OPENING_TYPE_OPTIONS}
                valueKey="value"
                labelKey="label"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
              <ToolbarFilterSelect
                label="Status"
                urlKey="status"
                allLabel="All statuses"
                options={OPENING_STATUS_OPTIONS}
                valueKey="value"
                labelKey="label"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
            </>
          }
          loading={loading}
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </Layout4>
  );
}

export default RecruitmentOpeningsList;
